import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Footer from '../components/Footer';
import '../styles/Apps.css';
import { 
  Plus, 
  MoreVertical, 
  Edit2, 
  Trash2, 
  User, 
  Lock, 
  ChevronDown,
  FolderPlus,
  PlusCircle,
  Clock,
  Share2,
  ExternalLink
} from 'react-feather';

interface Workspace {
  workspace_id: number;
  workspace_name: string;
}

interface App {
  app_id: number;
  app_name: string;
  description: string;
  app_type: string;
  status: string;
  workspace_id: number;
  creator: string;
  create_time?: string;
  update_time?: string;
  publish_uuid?: string | null;
  published_at?: string | null;
}

const Apps: React.FC = () => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<number | null>(null);
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateWorkspaceModalOpen, setIsCreateWorkspaceModalOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showAppMenu, setShowAppMenu] = useState<number | null>(null);
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const navigate = useNavigate();
  const { t } = useTranslation();

  // 格式化日期函数
  const formatDate = (dateString?: string): string => {
    if (!dateString) return t('apps.time.unknown');
    
    const date = new Date(dateString);
    
    // 判断日期是否有效
    if (isNaN(date.getTime())) return t('apps.time.unknown');
    
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 定义fetchApps函数，需要放在useEffect之前
  const fetchApps = useCallback(async (workspaceId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<App[]>(`https://ceshi.lightark.cc/api/v1/apps/?workspace_id=${workspaceId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // 按更新时间倒序排序
      const sortedApps = [...response.data].sort((a, b) => {
        // 如果没有更新时间，则使用创建时间
        const timeA = a.update_time || a.create_time || '';
        const timeB = b.update_time || b.create_time || '';
        
        // 倒序排列（新的在前）
        return new Date(timeB).getTime() - new Date(timeA).getTime();
      });
      
      setApps(sortedApps);
    } catch (err) {
      setError(t('apps.error.fetchApps'));
      console.error('Error fetching apps:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as Node;
      const isClickInsideMenu = Object.values(menuRefs.current).some(
        (menuRef) => menuRef && menuRef.contains(clickedElement)
      );
      
      if (!isClickInsideMenu) {
        setShowAppMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchApps(selectedWorkspace);
    }
  }, [selectedWorkspace, fetchApps]);

  const fetchWorkspaces = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://ceshi.lightark.cc/api/v1/workspaces/', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkspaces(data);
        if (data.length > 0) {
          setSelectedWorkspace(data[0].workspace_id);
          localStorage.setItem("workspace_id",data[0].workspace_id)
        }
      }
    } catch (error) {
      console.error('获取工作空间失败:', error);
    }
  };

  const handleWorkspaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'create') {
      setIsCreateWorkspaceModalOpen(true);
    } else {
      setSelectedWorkspace(Number(value));
    }
  };

  const handleAppClick = (appId: number) => {
    navigate(`/apps/detail/${appId}?workspace_id=${selectedWorkspace}`);
  };

  const handleCreateWorkspace = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://ceshi.lightark.cc/api/v1/workspaces/', {
        workspace_name: newWorkspaceName,
        description: '',
        visibility: 'private'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setNewWorkspaceName('');
      setIsCreateWorkspaceModalOpen(false);
      await fetchWorkspaces();
    } catch (error) {
      console.error('创建工作空间失败:', error);
      setError(t('apps.error.createWorkspace'));
    }
  };

  const handleAppMenuClick = (e: React.MouseEvent, appId: number) => {
    e.stopPropagation();
    setShowAppMenu(showAppMenu === appId ? null : appId);
  };

  const handleDeleteApp = async (e: React.MouseEvent, appId: number) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`https://ceshi.lightark.cc/api/v1/apps/${appId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setApps(apps.filter(app => app.app_id !== appId));
      setShowAppMenu(null);
    } catch (error) {
      console.error('删除应用失败:', error);
      setError(t('apps.error.deleteApp'));
    }
  };

  const handleEditApp = (e: React.MouseEvent, appId: number) => {
    e.stopPropagation();
    navigate(`/apps/edit/${appId}`);
    setShowAppMenu(null);
  };

  const handleOpenSharedApp = (e: React.MouseEvent, publishUuid: string | null | undefined) => {
    e.stopPropagation();
    if (publishUuid) {
      window.open(`/apps/run/${publishUuid}`, '_blank');
    }
    setShowAppMenu(null);
  };

  if (loading) {
    return <div className="apps-container">{t('apps.loading')}</div>;
  }

  if (error) {
    return <div className="apps-container">{error}</div>;
  }

  return (
    <div className="">
      <div className="apps-container">
        <div className="apps-header-actions">
          <Link to="/" className="app-logo">
            Ark App Builder
          </Link>
          <div className="workspace-dropdown">
            <select 
              value={selectedWorkspace || ''} 
              onChange={handleWorkspaceChange}
              aria-label={t('apps.workspace.select')}
            >
              {workspaces.map(workspace => (
                <option key={workspace.workspace_id} value={workspace.workspace_id}>
                  {workspace.workspace_name}
                </option>
              ))}
              <option value="create">+ {t('apps.workspace.create')}</option>
            </select>
          </div>
        </div>

        <div className="app-grid">
          {/* 创建应用卡片 */}
          <div 
            className="create-app-card"
            onClick={() => {
              if (selectedWorkspace) {
                navigate(`/apps/create?workspace_id=${selectedWorkspace}`);
              } else {
                setError(t('apps.error.fetchWorkspaces'));
              }
            }}
          >
            <div className="create-app-icon">
              <Plus size={24} />
            </div>
            <div className="create-app-text">
              {t('apps.create.button')}
            </div>
          </div>

          {/* 现有应用列表 */}
          {apps.map((app) => (
            <div
              key={app.app_id}
              className="app-card"
              onClick={() => handleAppClick(app.app_id)}
            >
              <div 
                className="more-actions" 
                onClick={(e) => handleAppMenuClick(e, app.app_id)}
              >
                <MoreVertical size={16} />
              </div>
              {showAppMenu === app.app_id && (
                <div 
                  className="app-menu" 
                  ref={(el) => menuRefs.current[app.app_id] = el}
                >
                  <div className="menu-item" onClick={(e) => handleEditApp(e, app.app_id)}>
                    <Edit2 size={16} />
                    {t('apps.app.menu.edit')}
                  </div>
                  {app.publish_uuid ? (
                    <div className="menu-item share" onClick={(e) => handleOpenSharedApp(e, app.publish_uuid)}>
                      <Share2 size={16} />
                      {t('apps.app.share.open')}
                    </div>
                  ) : (
                    <div className="menu-item disabled">
                      <Share2 size={16} />
                      {t('apps.app.share.notPublished')}
                    </div>
                  )}
                  <div className="menu-item delete" onClick={(e) => handleDeleteApp(e, app.app_id)}>
                    <Trash2 size={16} />
                    {t('apps.app.menu.delete')}
                  </div>
                </div>
              )}
              <h3 className="app-name">{app.app_name}</h3>
              <p className="creator">
                <User size={14} />
                {app.creator || t('apps.app.creator')}
              </p>
              <p className="update-time">
                <Clock size={14} />
                {t('apps.app.lastUpdate')}: {formatDate(app.update_time)}
              </p>
              <div className="app-card-footer">
                <span className="privacy-tag">
                  <Lock size={12} />
                  PRIVATE
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 创建工作空间的模态框 */}
      {isCreateWorkspaceModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>{t('apps.workspace.new.title')}</h2>
            <input
              type="text"
              value={newWorkspaceName}
              onChange={(e) => setNewWorkspaceName(e.target.value)}
              placeholder={t('apps.workspace.new.name')}
              aria-label={t('apps.workspace.new.name')}
            />
            <div className="modal-buttons">
              <button onClick={() => setIsCreateWorkspaceModalOpen(false)}>
                {t('apps.workspace.new.cancel')}
              </button>
              <button onClick={handleCreateWorkspace}>
                {t('apps.workspace.new.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Apps; 
